/**
 * clickt brand tokens, source of truth, ingested from
 * `brand-guidelines/clickt-link-brand-guide.pdf` v1.0 · 2026.
 *
 * Any surface that needs brand values (Tailwind preset, marketing components,
 * dashboard charts, OG images) reads from here. CSS-side mirrors of these
 * tokens live in each app's globals.css `:root` block, keep in sync.
 */

export const colors = {
  // Primary
  midnight: '#020B1D',
  electricBlue: '#2F5BFF',
  deepSapphire: '#163FD6',

  // Secondary
  coolGray: '#6F7785',
  lightSilver: '#E8EBF2',
  white: '#FFFFFF',

  // Accent / semantic
  successGreen: '#1DBF73',
  alertOrange: '#FF8A34',
  signalRed: '#FF4D5A',

  // Derived surfaces
  midnightDeep: '#0F1D40', // gradient stop
  borderSubtle: '#D7DDEA', // input border per brand spec
  textMuted: '#6F7785',
} as const;

export const gradients = {
  primary: 'linear-gradient(135deg, #163FD6 0%, #2F5BFF 100%)',
  premiumDark: 'linear-gradient(135deg, #020B1D 0%, #0F1D40 100%)',
  glassBlue: 'linear-gradient(135deg, rgba(47, 91, 255, 0.15) 0%, rgba(22, 63, 214, 0.05) 100%)',
} as const;

export const fontFamilies = {
  // Inter is the production font (Satoshi as a heading override only if licensed).
  sans: '"Inter", "Inter Fallback", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  display:
    '"Satoshi", "Inter", "Inter Fallback", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  mono: 'ui-monospace, SFMono-Regular, "JetBrains Mono", Menlo, monospace',
} as const;

/**
 * Type scale (px values for clarity; emitted as rem in CSS).
 * Mobile scaling lives in component-level `clamp()` calls, not here.
 */
export const typeScale = {
  h1: { size: '4rem', weight: 700, lineHeight: 1.05, letterSpacing: '-0.02em' },
  h2: { size: '3rem', weight: 700, lineHeight: 1.1, letterSpacing: '-0.01em' },
  h3: { size: '2.25rem', weight: 600, lineHeight: 1.2, letterSpacing: '-0.005em' },
  h4: { size: '1.75rem', weight: 600, lineHeight: 1.3, letterSpacing: '0' },
  bodyLg: { size: '1.25rem', weight: 400, lineHeight: 1.7, letterSpacing: '0' },
  body: { size: '1rem', weight: 400, lineHeight: 1.7, letterSpacing: '0' },
  caption: { size: '0.8125rem', weight: 500, lineHeight: 1.4, letterSpacing: '0.08em' },
} as const;

export const radii = {
  sm: '10px',
  md: '16px',
  lg: '24px',
  pill: '9999px',
  // Component-specific (per brand PDF spec)
  button: '14px',
  input: '14px',
  card: '24px',
} as const;

export const shadows = {
  soft: '0 10px 30px rgba(2, 11, 29, 0.06)',
  hover: '0 15px 40px rgba(2, 11, 29, 0.12)',
  focusRing: '0 0 0 4px rgba(47, 91, 255, 0.18)',
} as const;

export const motion = {
  fast: '150ms',
  standard: '250ms',
  large: '400ms',
  easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
} as const;

export const breakpoints = {
  sm: '375px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1440px',
} as const;

export const spacing = {
  unit: '0.25rem', // 4px base
} as const;
